import type { ComponentProps } from "react";
import type { FoodsIcon } from "@foodpilot/foods";

export type ReactionIconName = ComponentProps<typeof FoodsIcon>["icon"];

export type ReactionMuiIconName = "emojiEmotions";

export type ReactionIconConfig = {
    icon: ReactionIconName;
    muiIcon?: ReactionMuiIconName;
};

export interface CreateReactionData {
    postId: number;
    type: "like" | "celebrate" | "support" | "insightful" | "funny";
}

export interface ReactionResponse {
    success: boolean;
    action: "added" | "removed";
    reaction: {
        id: number;
        type: string;
        createdAt: string;
    } | null;
    reactionSummary: Record<string, number>;
    totalReactions: number;
}

export const REACTION_TYPES = [
    {
        type: "like",
        icon: "starFilled" satisfies ReactionIconName,
        inactiveIcon: "starEmpty" satisfies ReactionIconName,
        labelKey: "social.reactions.like",
        defaultLabel: "Like",
        color: "#E69500",
    },
    {
        type: "celebrate",
        icon: "congrats" satisfies ReactionIconName,
        inactiveIcon: "congrats" satisfies ReactionIconName,
        labelKey: "social.reactions.celebrate",
        defaultLabel: "Celebrate",
        color: "#FF9800",
    },
    {
        type: "support",
        icon: "handshake" satisfies ReactionIconName,
        inactiveIcon: "handshake" satisfies ReactionIconName,
        labelKey: "social.reactions.support",
        defaultLabel: "Support",
        color: "#4CAF50",
    },
    {
        type: "insightful",
        icon: "suggestions" satisfies ReactionIconName,
        inactiveIcon: "suggestions" satisfies ReactionIconName,
        labelKey: "social.reactions.insightful",
        defaultLabel: "Insightful",
        color: "#2196F3",
    },
    {
        type: "funny",
        icon: "cheer" satisfies ReactionIconName,
        muiIcon: "emojiEmotions",
        inactiveIcon: "cheer" satisfies ReactionIconName,
        labelKey: "social.reactions.funny",
        defaultLabel: "Funny",
        color: "#FFC107",
    },
] as const;

export type ReactionType = (typeof REACTION_TYPES)[number]["type"];

export type ReactionDefinition = (typeof REACTION_TYPES)[number];

export const getReactionConfig = (type: string): ReactionDefinition | undefined =>
    REACTION_TYPES.find((reaction) => reaction.type === type);
